// store/slices/serviceRequestSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import { IS_DEVELOPMENT } from "../../utils/constant";

// Fetch service requests
export const fetchServiceRequests = createAsyncThunk(
  "serviceRequest/fetchServiceRequests",
  async (
    { page = 1, perPage = 10, sortBy = "created_at", sortOrder = "desc" },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.getServiceRequests({
        page,
        perPage,
        sortBy,
        sortOrder,
      });
      if (response.success === true) {
        return response.data;
      }
      return rejectWithValue(
        response.message || "Failed to fetch service requests",
      );
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to load service requests",
      );
    }
  },
);
export const fetchCancelRescheduleReasons = createAsyncThunk(
  "serviceRequest/fetchCancelRescheduleReasons",
  async ({ type } = {}, { rejectWithValue }) => {
    try {
      const params = type ? { type } : {};
      const response = await api.getCancelRescheduleReasons(params);
      if (response.success === true) {
        return response.data || [];
      }
      return rejectWithValue(response.message || "Failed to fetch reasons");
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load reasons");
    }
  },
);

// Reschedule service request
export const rescheduleServiceRequest = createAsyncThunk(
  "serviceRequest/rescheduleServiceRequest",
  async ({ serviceId, payload }, { rejectWithValue }) => {
    try {
      const response = await api.rescheduleServiceRequest(serviceId, payload);
      if (response.success === true) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to reschedule");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reschedule service",
      );
    }
  },
);

// Cancel service request
export const cancelServiceRequest = createAsyncThunk(
  "serviceRequest/cancelServiceRequest",
  async ({ serviceId, payload }, { rejectWithValue }) => {
    try {
      const response = await api.cancelServiceRequest(serviceId, payload);
      if (response.success === true) {
        return response.data;
      }
      return rejectWithValue(response.message || "Failed to cancel service");
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel service",
      );
    }
  },
);

const initialState = {
  orders: [],
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  reasons: {
    cancel: [],
    reschedule: [],
  },
  currentService: null,
  loading: false,
  detailsLoading: false,
  error: null,
};

const serviceRequestSlice = createSlice({
  name: "serviceRequest",
  initialState,
  reducers: {
    clearServiceDetails: (state) => {
      state.currentService = null;
    },
    clearOrders: (state) => {
      state.orders = [];
      state.pagination = initialState.pagination;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch service requests
      .addCase(fetchServiceRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServiceRequests.fulfilled, (state, action) => {
        state.loading = false;
        // Handle the response structure - data is an array of service requests
        const data = action.payload;
        state.orders = Array.isArray(data) ? data : [];
        state.pagination = {
          current: action.meta?.arg?.page || 1,
          pageSize: action.meta?.arg?.perPage || 10,
          total: state.orders.length,
        };
      })
      .addCase(fetchServiceRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.orders = [];
      })
      // Fetch reasons
      .addCase(fetchCancelRescheduleReasons.fulfilled, (state, action) => {
        state.reasons = action.payload;
      })
      .addCase(fetchCancelRescheduleReasons.rejected, (state) => {
        state.reasons = { cancel: [], reschedule: [] };
      })
      // Reschedule
      .addCase(rescheduleServiceRequest.fulfilled, (state) => {
        // Success handled by component
      })
      .addCase(rescheduleServiceRequest.rejected, (state, action) => {
        if (IS_DEVELOPMENT) {
          console.error("Reschedule failed:", action.payload);
        }
      })
      // Cancel
      .addCase(cancelServiceRequest.fulfilled, (state) => {
        // Success handled by component
      })
      .addCase(cancelServiceRequest.rejected, (state, action) => {
        if (IS_DEVELOPMENT) {
          console.error("Cancel failed:", action.payload);
        }
      });
  },
});

export const { clearServiceDetails, clearOrders, setPagination } =
  serviceRequestSlice.actions;
export default serviceRequestSlice.reducer;